<template>
  <div class="mask" @click.self="onClickCancel">
    <div
      ref="dialogRoot"
      class="dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="dialog__header">
        <h2 :id="titleId" class="dialog__title">
          {{ $t("proofreadingFishConfirmDialog.title") }}
        </h2>
      </header>

      <div class="dialog__body">
        <p class="dialog__lead">
          {{ $t("proofreadingFishConfirmDialog.lead") }}
        </p>

        <div ref="calcWrap" class="dialog__calc">
          <button
            type="button"
            class="dialog__calc-link"
            :aria-expanded="isCalcTipOpen ? 'true' : 'false'"
            :aria-controls="calcTipId"
            @click.stop="onToggleCalcTip"
          >
            <IconInfo />
            {{ $t("proofreadingFishConfirmDialog.calcLink") }}
          </button>
          <div
            v-if="isCalcTipOpen"
            :id="calcTipId"
            class="dialog__calc-tip"
            role="tooltip"
          >
            <p class="dialog__calc-tip-title">
              {{ $t("proofreadingFishConfirmDialog.calcTipTitle") }}
            </p>
            <ul class="dialog__calc-tip-list">
              <li
                v-for="(row, index) in tierRows"
                :key="index"
                class="dialog__calc-tip-row"
              >
                <span class="dialog__calc-tip-range">{{ row.range }}</span>
                <span class="dialog__calc-tip-sep" aria-hidden="true">：</span>
                <span class="dialog__calc-tip-amount">{{ row.amount }}</span>
              </li>
            </ul>
            <p v-if="isSessionContinuation" class="dialog__calc-tip-note">
              {{ $t("proofreadingFishConfirmDialog.sessionNote") }}
            </p>
          </div>
        </div>

        <p class="dialog__cost">
          <i18n :path="costI18nPath" tag="span">
            <strong place="amount" class="dialog__cost-amount">{{ formattedAmount }} fish</strong>
          </i18n>
        </p>
      </div>

      <footer class="dialog__footer">
        <button type="button" class="dialog__cancel-button" @click="onClickCancel">
          {{ $t("proofreadingFishConfirmDialog.cancel") }}
        </button>
        <button type="button" class="dialog__ok-button" @click="onClickConfirm">
          {{ $t("proofreadingFishConfirmDialog.confirm") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import {
  formatProofreadingFishNumber,
  getProofreadingFishTierLineParams,
  PROOFREADING_FISH_TIERS,
} from "@/lib/utils/proofreadingFishTiers";
import IconInfo from "~icons/figma/Info";

export type ProofreadingFishConfirmDialogResult = boolean;

export type ProofreadingFishConfirmDialogOptions = {
  /** 今回かかる Fish（全額または差額） */
  chargeFish: number;
  /** 現在の所持 Fish */
  currentFish: number;
  /** 支払い済みセッション継続中か（差額課金のとき true） */
  isSessionContinuation?: boolean;
};

export default Vue.extend({
  components: {
    IconInfo,
  },
  props: {
    chargeFish: {
      type: Number,
      required: true,
    },
    currentFish: {
      type: Number,
      required: true,
    },
    isSessionContinuation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      titleId: "proofreading-fish-confirm-dialog-title",
      calcTipId: "proofreading-fish-confirm-calc-tip",
      isCalcTipOpen: false,
    };
  },
  computed: {
    numberLocale(): string {
      return this.$i18n.locale || "ja";
    },
    formattedAmount(): string {
      return formatProofreadingFishNumber(this.chargeFish, this.numberLocale);
    },
    costI18nPath(): string {
      return this.isSessionContinuation
        ? "proofreadingFishConfirmDialog.costAdditional"
        : "proofreadingFishConfirmDialog.cost";
    },
    tierRows(): { range: string; amount: string }[] {
      return PROOFREADING_FISH_TIERS.map((tier) => {
        const params = getProofreadingFishTierLineParams(tier, this.numberLocale);
        return {
          range: this.$t("proofreadingFishConfirmDialog.tierRange", params) as string,
          amount: this.$t("proofreadingFishConfirmDialog.tierAmount", params) as string,
        };
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      const root = this.$refs.dialogRoot as HTMLElement | undefined;
      root?.focus();
    });
    document.addEventListener("keydown", this.onKeydown);
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    document.removeEventListener("click", this.onDocumentClick);
  },
  methods: {
    onToggleCalcTip() {
      this.isCalcTipOpen = !this.isCalcTipOpen;
    },
    onDocumentClick(e: MouseEvent) {
      if (!this.isCalcTipOpen) return;
      const wrap = this.$refs.calcWrap as HTMLElement | undefined;
      if (wrap && !wrap.contains(e.target as Node)) {
        this.isCalcTipOpen = false;
      }
    },
    onKeydown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (this.isCalcTipOpen) {
        e.preventDefault();
        this.isCalcTipOpen = false;
        return;
      }
      this.onClickCancel();
    },
    onClickConfirm() {
      this.$close(true as ProofreadingFishConfirmDialogResult);
    },
    onClickCancel() {
      this.$close(false as ProofreadingFishConfirmDialogResult);
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9300;
  background-color: var(--color-overlay);
}

.dialog {
  display: flex;
  flex-direction: column;
  width: min(400px, 92vw);
  margin: 0;
  border: none;
  background-color: var(--color-background-normal);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
  overflow: visible;

  &:focus {
    outline: none;
  }
}

.dialog__header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 20px;
}

.dialog__title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  text-align: center;
  line-height: var(--line-height-default);
}

.dialog__body {
  padding: 24px 24px 8px;
}

.dialog__lead {
  margin: 0;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.dialog__calc {
  position: relative;
  margin-top: 8px;
}

.dialog__cost {
  margin: 28px 0 0;
  text-align: center;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-default);
  color: var(--color-text-normal);
}

.dialog__cost-amount {
  font-weight: var(--font-weight-bold);
}

.dialog__calc-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  padding: 0;
  background: transparent;
  color: var(--color-primary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.dialog__calc-tip {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 10;
  width: max-content;
  max-width: 360px;
  padding: 10px 12px;
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  pointer-events: none;
}

.dialog__calc-tip-title {
  margin: 0 0 4px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: 1.5;
  color: var(--color-text-normal);
}

.dialog__calc-tip-list {
  display: table;
  border-collapse: collapse;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--font-size-sm);
  line-height: 1.7;
  color: var(--color-text-normal);
}

.dialog__calc-tip-row {
  display: table-row;
}

.dialog__calc-tip-range,
.dialog__calc-tip-sep,
.dialog__calc-tip-amount {
  display: table-cell;
  white-space: nowrap;
}

.dialog__calc-tip-sep {
  padding: 0 0.4em;
  white-space: nowrap;
}

.dialog__calc-tip-amount {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.dialog__calc-tip-note {
  margin: 8px 0 0;
  font-size: var(--font-size-xs);
  line-height: 1.5;
  color: var(--color-text-low);
  white-space: normal;
}

.dialog__footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px 24px;
}

.dialog__cancel-button,
.dialog__ok-button {
  position: relative;
  flex: 1 1 0;
  min-width: 120px;
  padding: 10px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;
}

.dialog__cancel-button {
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
}

.dialog__ok-button {
  background-color: var(--color-primary);
  color: var(--color-text-invert);
}
</style>
